import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, X, Truck, PackageCheck, ListChecks, Boxes, Send } from "lucide-react";
import { api, ApiClientError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast } from "../lib/toast";
import { DataTable, type Column } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { KpiTile } from "../components/KpiTile";
import type { FulfillmentStatus, OrderType, OrderStatus, NotificationStatus, Shipment } from "@sbd-tcic/shared-types";

interface ShipmentQueueRow {
  orderNumber: string;
  orderType: OrderType;
  customerName: string;
  orderDate: string;
  createdBy: string;
  repairStatus: string;
  paymentStatus: "Paid" | "Pending";
  total: number;
  method: string;
  fulfillmentStatus: FulfillmentStatus;
}

interface ShippingOrderDto {
  orderNumber: string;
  orderType: OrderType;
  orderMethod: string;
  serviceType: string;
  orderDate: string;
  customerId: string;
  customerName: string;
  value: number;
  status: OrderStatus;
  fulfillmentStatus: FulfillmentStatus;
  paymentMethod: string;
  notificationStatus: NotificationStatus;
  createdBy: string;
  reason: string;
}

interface ShippingRepairOrderDto {
  id: string;
  shipTo: string;
  equipment: string;
  modelNumber: string;
  serialNumber: string;
  status: string;
  technician: string | null;
  freightMethod: string;
  deliveryMethod: string;
}

interface ShippingLineItemDto {
  partNumber: string;
  description: string;
  qty: number;
  listPrice?: number;
  yourPrice?: number;
  extPrice?: number;
  stdCost?: number;
}

interface ShippingOrderDetailDto {
  order: ShippingOrderDto;
  repairOrder: ShippingRepairOrderDto | null;
  lineItems: ShippingLineItemDto[];
  shipment: Shipment | null;
}

interface MarkShippedResult {
  orderNumber: string;
  fulfillmentStatus: "Shipped";
  trackingNumber: string | null;
  carrier: string | null;
}

interface TrackingResult {
  orderNumber: string;
  trackingNumber: string;
  carrier: string;
}

const money = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TABS = ["All", "Ready to Ship", "In Picking", "Shipped"] as const;
type Tab = (typeof TABS)[number];

function canMarkShipped(status: FulfillmentStatus): boolean {
  return status === "Ready to Ship" || status === "In Picking";
}

export function Shipping() {
  const { can } = useAuth();
  const { toast } = useToast();
  const manage = can("shipping.manage");

  const [rows, setRows] = useState<ShipmentQueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>("Ready to Ship");
  const [keyword, setKeyword] = useState("");

  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string | null>(null);
  const [detail, setDetail] = useState<ShippingOrderDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const [shipTrackingNumber, setShipTrackingNumber] = useState("");
  const [shipCarrier, setShipCarrier] = useState("");
  const [shipMethod, setShipMethod] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingCarrier, setTrackingCarrier] = useState("");

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<ShipmentQueueRow[]>("/shipping/queue");
      setRows(data);
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to load the shipping queue.", "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const filteredRows = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (tab === "All" || r.fulfillmentStatus === tab) &&
        (!kw || (r.orderNumber + r.customerName).toLowerCase().includes(kw)),
    );
  }, [rows, tab, keyword]);

  const counts = useMemo(() => {
    const readyToShip = rows.filter((r) => r.fulfillmentStatus === "Ready to Ship").length;
    const inPicking = rows.filter((r) => r.fulfillmentStatus === "In Picking").length;
    const shipped = rows.filter((r) => r.fulfillmentStatus === "Shipped").length;
    return { readyToShip, inPicking, shipped, total: rows.length };
  }, [rows]);

  const loadDetail = useCallback(
    async (orderNumber: string) => {
      setDetailLoading(true);
      try {
        const data = await api.get<ShippingOrderDetailDto>(`/shipping/orders/${orderNumber}`);
        setDetail(data);
        setShipTrackingNumber(data.shipment?.trackingNumber ?? "");
        setShipCarrier(data.shipment?.carrier ?? "");
        setShipMethod(data.shipment?.method ?? data.order.orderMethod ?? "");
        setTrackingNumber(data.shipment?.trackingNumber ?? "");
        setTrackingCarrier(data.shipment?.carrier ?? "");
      } catch (err) {
        toast(err instanceof ApiClientError ? err.message : "Unable to load order detail.", "error");
        setSelectedOrderNumber(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [toast],
  );

  function openOrder(orderNumber: string) {
    setSelectedOrderNumber(orderNumber);
    void loadDetail(orderNumber);
  }

  function closeDrawer() {
    setSelectedOrderNumber(null);
    setDetail(null);
  }

  async function markShipped() {
    if (!selectedOrderNumber) return;
    setBusy(true);
    try {
      await api.post<MarkShippedResult>(`/shipping/orders/${selectedOrderNumber}/mark-shipped`, {
        trackingNumber: shipTrackingNumber.trim() || undefined,
        carrier: shipCarrier.trim() || undefined,
        method: shipMethod.trim() || undefined,
      });
      toast(`Order ${selectedOrderNumber} marked Shipped.`, "success");
      await Promise.all([loadQueue(), loadDetail(selectedOrderNumber)]);
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to mark this order Shipped.", "error");
    } finally {
      setBusy(false);
    }
  }

  async function saveTracking() {
    if (!selectedOrderNumber || !trackingNumber.trim() || !trackingCarrier.trim()) return;
    setBusy(true);
    try {
      await api.patch<TrackingResult>(`/shipping/orders/${selectedOrderNumber}/tracking`, {
        trackingNumber: trackingNumber.trim(),
        carrier: trackingCarrier.trim(),
      });
      toast(`Tracking updated for ${selectedOrderNumber}.`, "success");
      await loadDetail(selectedOrderNumber);
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to update tracking.", "error");
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<ShipmentQueueRow>[] = [
    { key: "orderNumber", header: "Order #", render: (r) => <span className="font-bold text-brand-blue">{r.orderNumber}</span> },
    { key: "orderType", header: "Type", render: (r) => r.orderType },
    { key: "customerName", header: "Customer", render: (r) => r.customerName },
    { key: "orderDate", header: "Order Date", render: (r) => r.orderDate },
    { key: "repairStatus", header: "Repair Status", render: (r) => r.repairStatus },
    { key: "paymentStatus", header: "Pay Status", render: (r) => r.paymentStatus },
    { key: "total", header: "Total", render: (r) => money(r.total) },
    { key: "method", header: "Method", render: (r) => r.method },
    { key: "fulfillmentStatus", header: "Fulfillment", render: (r) => <StatusBadge status={r.fulfillmentStatus} /> },
  ];

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h2 className="text-sm font-bold text-ink-muted uppercase tracking-wide mb-3">Shipping Productivity</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
          <KpiTile icon={Boxes} iconColor="blue" label="Total In Queue" value={counts.total} />
          <KpiTile icon={ListChecks} iconColor="orange" label="Ready to Ship" value={counts.readyToShip} />
          <KpiTile icon={Truck} iconColor="purple" label="In Picking" value={counts.inPicking} />
          <KpiTile icon={PackageCheck} iconColor="green" label="Shipped" value={counts.shipped} />
        </div>
      </section>

      <section className="card p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3.5">
          <div className="flex items-center gap-1.5 bg-surface rounded-md p-1">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${
                  tab === t ? "bg-brand-blue text-white" : "text-ink-muted hover:text-ink"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input
              className="input pl-8"
              placeholder="Search order # or customer…"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>
        </div>
        {loading ? (
          <div className="text-ink-muted text-sm py-10 text-center">Loading shipping queue…</div>
        ) : (
          <DataTable columns={columns} rows={filteredRows} rowKey={(r) => r.orderNumber} onRowClick={(r) => openOrder(r.orderNumber)} emptyMessage="No orders match this view." />
        )}
      </section>

      {selectedOrderNumber && (
        <div className="fixed inset-0 z-[100] flex justify-end bg-black/40" onClick={closeDrawer}>
          <div className="w-full max-w-lg h-full bg-card overflow-y-auto shadow-pop" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-card z-10">
              <h2 className="text-base font-extrabold">{selectedOrderNumber}</h2>
              <button aria-label="Close" className="text-ink-muted hover:text-ink" onClick={closeDrawer}>
                <X size={18} />
              </button>
            </div>

            <div className="p-5 flex flex-col gap-5">
              {detailLoading || !detail ? (
                <div className="text-ink-muted text-sm py-10 text-center">Loading order detail…</div>
              ) : (
                <>
                  <div className="card p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-bold text-sm">{detail.order.customerName}</div>
                      <StatusBadge status={detail.order.fulfillmentStatus} />
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <div className="text-ink-muted text-xs">Order Type</div>
                        <div className="font-semibold">{detail.order.orderType}</div>
                      </div>
                      <div>
                        <div className="text-ink-muted text-xs">Order Date</div>
                        <div className="font-semibold">{detail.order.orderDate}</div>
                      </div>
                      <div>
                        <div className="text-ink-muted text-xs">Order Method</div>
                        <div className="font-semibold">{detail.order.orderMethod}</div>
                      </div>
                      <div>
                        <div className="text-ink-muted text-xs">Total</div>
                        <div className="font-semibold">{money(detail.order.value)}</div>
                      </div>
                      <div>
                        <div className="text-ink-muted text-xs">Payment Method</div>
                        <div className="font-semibold">{detail.order.paymentMethod}</div>
                      </div>
                      <div>
                        <div className="text-ink-muted text-xs">Notification</div>
                        <div className="font-semibold">{detail.order.notificationStatus}</div>
                      </div>
                    </div>
                  </div>

                  {detail.repairOrder && (
                    <div className="card p-4">
                      <h3 className="text-xs font-bold uppercase tracking-wide text-ink-muted mb-2.5">Repair Order</h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <div className="text-ink-muted text-xs">Ship To</div>
                          <div className="font-semibold">{detail.repairOrder.shipTo}</div>
                        </div>
                        <div>
                          <div className="text-ink-muted text-xs">Equipment</div>
                          <div className="font-semibold">
                            {detail.repairOrder.equipment} ({detail.repairOrder.modelNumber})
                          </div>
                        </div>
                        <div>
                          <div className="text-ink-muted text-xs">Repair Status</div>
                          <div className="font-semibold">{detail.repairOrder.status}</div>
                        </div>
                        <div>
                          <div className="text-ink-muted text-xs">Freight Method</div>
                          <div className="font-semibold">{detail.repairOrder.freightMethod}</div>
                        </div>
                        <div>
                          <div className="text-ink-muted text-xs">Technician</div>
                          <div className="font-semibold">{detail.repairOrder.technician ?? "Unassigned"}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="card p-4">
                    <h3 className="text-xs font-bold uppercase tracking-wide text-ink-muted mb-2.5">
                      {detail.repairOrder ? "Parts Used" : "Line Items"}
                    </h3>
                    {detail.lineItems.length === 0 ? (
                      <div className="text-sm text-ink-muted text-center py-3">No items.</div>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        {detail.lineItems.map((li) => (
                          <div key={li.partNumber} className="flex items-center justify-between text-sm border-b border-border last:border-0 py-1.5">
                            <span>
                              {li.partNumber} — {li.description}
                            </span>
                            <span className="font-semibold">{li.qty} × {money(li.listPrice ?? 0)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="card p-4">
                    <h3 className="text-xs font-bold uppercase tracking-wide text-ink-muted mb-2.5 flex items-center gap-1.5">
                      <Truck size={14} /> Shipment
                    </h3>
                    {detail.shipment ? (
                      <div className="grid grid-cols-2 gap-3 text-sm mb-3.5">
                        <div>
                          <div className="text-ink-muted text-xs">Tracking #</div>
                          <div className="font-semibold">{detail.shipment.trackingNumber ?? "—"}</div>
                        </div>
                        <div>
                          <div className="text-ink-muted text-xs">Carrier</div>
                          <div className="font-semibold">{detail.shipment.carrier ?? "—"}</div>
                        </div>
                        <div>
                          <div className="text-ink-muted text-xs">Method</div>
                          <div className="font-semibold">{detail.shipment.method}</div>
                        </div>
                        <div>
                          <div className="text-ink-muted text-xs">Shipped Date</div>
                          <div className="font-semibold">{detail.shipment.shippedDate ?? "Not yet shipped"}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-ink-muted mb-3.5">No shipment record yet.</div>
                    )}

                    {manage && (
                      <>
                        {canMarkShipped(detail.order.fulfillmentStatus) && (
                          <div className="border-t border-border pt-3.5 mb-3.5">
                            <div className="text-xs font-bold uppercase tracking-wide text-ink-muted mb-2">Mark Shipped</div>
                            <div className="grid grid-cols-3 gap-2 mb-2.5">
                              <input className="input" placeholder="Tracking #" value={shipTrackingNumber} onChange={(e) => setShipTrackingNumber(e.target.value)} />
                              <input className="input" placeholder="Carrier" value={shipCarrier} onChange={(e) => setShipCarrier(e.target.value)} />
                              <input className="input" placeholder="Method" value={shipMethod} onChange={(e) => setShipMethod(e.target.value)} />
                            </div>
                            <button className="btn-primary w-full justify-center" disabled={busy} onClick={() => void markShipped()}>
                              <Send size={15} /> {busy ? "Marking Shipped…" : "Mark Shipped"}
                            </button>
                          </div>
                        )}

                        <div className={canMarkShipped(detail.order.fulfillmentStatus) ? "border-t border-border pt-3.5" : ""}>
                          <div className="text-xs font-bold uppercase tracking-wide text-ink-muted mb-2">Add / Update Tracking</div>
                          <div className="grid grid-cols-2 gap-2 mb-2.5">
                            <input className="input" placeholder="Tracking #" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} />
                            <input className="input" placeholder="Carrier" value={trackingCarrier} onChange={(e) => setTrackingCarrier(e.target.value)} />
                          </div>
                          <button
                            className="btn-outline w-full justify-center"
                            disabled={busy || !trackingNumber.trim() || !trackingCarrier.trim()}
                            onClick={() => void saveTracking()}
                          >
                            {busy ? "Saving…" : "Save Tracking"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
